/**
 * Maps Supabase Auth User → app-facing SupabaseAuthUser.
 * Kept pure for tests (no React).
 */

import type { User } from '@supabase/supabase-js'

export interface SupabaseAuthUser {
  id: string
  firstName: string | null
  lastName: string | null
  username: string | null
  primaryEmailAddress: { emailAddress: string } | null
  imageUrl?: string | null
  isAnonymous: boolean
}

export function mapSupabaseUser(u: User | null): SupabaseAuthUser | null {
  if (!u) return null
  const meta = u.user_metadata ?? {}
  const email = u.email ?? meta.email ?? null
  const username =
    meta.user_name ??
    meta.preferred_username ??
    meta.username ??
    (email ? email.split('@')[0] : null)
  let firstName: string | null = meta.first_name ?? meta.given_name ?? null
  let lastName: string | null = meta.last_name ?? meta.family_name ?? null
  const fullFromMeta =
    typeof meta.full_name === 'string'
      ? meta.full_name.trim()
      : typeof meta.name === 'string'
        ? meta.name.trim()
        : ''

  /** When first_name was set explicitly (e.g. display-name save), do not infer lastName from stale full_name/name. */
  const hasExplicitAuthFirstName =
    typeof meta.first_name === 'string' && meta.first_name.trim() !== ''

  if (fullFromMeta && (!firstName || !lastName)) {
    if (hasExplicitAuthFirstName && firstName && !lastName) {
      // single-word / first-only display name
    } else {
      const parts = fullFromMeta.split(/\s+/).filter(Boolean)
      if (parts.length > 0) {
        if (!firstName) firstName = parts[0]
        if (!lastName && parts.length > 1) lastName = parts.slice(1).join(' ')
      }
    }
  }
  return {
    id: u.id,
    firstName,
    lastName,
    username,
    primaryEmailAddress: email ? { emailAddress: email } : null,
    imageUrl: meta.avatar_url ?? meta.picture ?? null,
    isAnonymous: u.is_anonymous === true,
  }
}
